import { t } from 'i18n';
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { NoProductsFound } from '@teko/ecommerce';
import { BlockHeader, Card, Container, styled, WebpImage } from '@teko/ui';
import { useMenuItems, useBanners, useTenantConfig, useSearch } from 'hooks';
import { MenuItem } from '@teko/discovery';
import { BannerSlides } from 'components/desktop/organisms/BannerSlides';
import { ProductCarousel } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { HeadSection } from 'components/HeadSection';
import { getUrlObject, resolveTekoPathname } from '@teko/utils';
import { useRouterEffect } from 'contexts';
import { SearchV2Params } from '@teko/discovery/v2';
import { Loading } from 'components/Loading';

const Wrapper = styled(Container)`
  background-color: #fff;
  margin: 2rem auto;
`;
const BannerProductGroup = styled.div`
  background-color: #fff;
`;
const ImageWrapper = styled.div`
  padding: 8px 16px 16px 0;
  text-align: center;
`;
const StyledImage = styled(WebpImage)`
  width: 60%;
  cursor: pointer;
`;
const StyledNoProductsFound = styled(NoProductsFound)`
  margin: 0;
  padding: 8rem;
`;
export const LandingPage: React.FunctionComponent = () => {
  const router = useRouter();
  const { baseDomain, canonicalDomain } = useTenantConfig();
  const { result: allMenuItems, isLoading } = useMenuItems(
    `landing-banner-product-${router.query.id}`,
  );
  const { banners, loading: bannersLoading } = useBanners(
    `landing-banner-${router.query.id}`,
  );
  const showPageNotFound =
    !isLoading &&
    !bannersLoading &&
    !allMenuItems.parents.length &&
    !banners.length;
  const { restoreScrollPos } = useRouterEffect();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      restoreScrollPos(router.asPath);
    }
  }, [banners.length, router]);

  const onClickBanner = async (
    urlType: string,
    targetPath: string,
    url?: string,
  ) => {
    const href = url
      ? url
      : targetPath && targetPath.startsWith('/')
      ? targetPath
      : null;
    if (href) {
      const { pathname, query } = await resolveTekoPathname(getUrlObject(href));
      router
        .push({ pathname: pathname || href, query }, href)
        .then(() => window.scrollTo(0, 0));
      return;
    }
    if (urlType === 'rewrite') {
      router
        .push({
          pathname: '/search',
          search: `?router=productListing&${targetPath}`,
        })
        .then(() => window.scrollTo(0, 0));
      return;
    }
    window.location.href = targetPath;
  };
  return (
    <Wrapper>
      <HeadSection
        canonical={`${canonicalDomain || baseDomain}/landing/${
          router.query.id
        }`}
      />
      <Loading isLoading={isLoading || bannersLoading}>
        {showPageNotFound ? (
          <StyledNoProductsFound text={t('Trang không tồn tại')} show />
        ) : (
          <>
            {!!banners?.length && (
              <BannerSlides banners={banners} imageWidth={1232} />
            )}
            {!!allMenuItems.parents.length && (
              <BannerProductGroup>
                {allMenuItems.parents.map((item: MenuItem) => {
                  return (
                    <React.Fragment key={item.id}>
                      {item.icon && (
                        <ImageWrapper
                          {...getTrackingData({
                            isTrackContent: true,
                            regionName: 'landingBannerProducts',
                            contentName: item.name,
                          })}
                        >
                          <StyledImage
                            width={1232}
                            onClick={() =>
                              onClickBanner(
                                item.urlType,
                                item.targetPath,
                                item.url,
                              )
                            }
                            src={item.icon}
                            alt={item.name}
                          />
                        </ImageWrapper>
                      )}

                      {!!item.children?.length ? (
                        <LandingProductCarousel
                          slug={
                            item.children[0].urlType === 'rewrite'
                              ? item.children[0].url || ''
                              : ''
                          }
                          title={item.children[0].name}
                          url={item.children[0].url || ''}
                        />
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </BannerProductGroup>
            )}
          </>
        )}
      </Loading>
    </Wrapper>
  );
};

const LandingProductCarousel = ({
  slug,
  title,
  url,
}: {
  slug: string;
  title: string;
  url: string;
}) => {
  const convertSearchParam: SearchV2Params = {
    slug: slug,
  };
  const { result, isLoading } = useSearch(convertSearchParam);
  if (!result.products.length && !isLoading) return null;
  return (
    <Card
      header={
        <BlockHeader
          hasDivider
          title={title}
          actionLink={{
            title: 'Xem tất cả',
            href: url,
            color: '#333',
            hover: 'underline',
          }}
        />
      }
    >
      <ProductCarousel
        spaceBetween={12}
        productsPerRow={5}
        products={result.products}
        regionName="landingProductBlocks"
      />
    </Card>
  );
};
